/**
 * The platform's marks and name, read once per page load (domain/branding.js).
 *
 * A store rather than a context: the landing page and the patient's public
 * pages sit outside the app's providers (App.jsx) and draw the marks too.
 *
 * The last answer is kept in localStorage so a returning visitor's screens
 * draw the right marks on the first frame. A visitor with nothing kept draws
 * an empty space of the same size until the answer arrives — never the
 * built-in mark first and the uploaded one a moment later.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { API_BASE_URL } from '../api/client';
import { brandingApi } from '../api/endpoints';
import { BUILT_IN, resolveBranding } from '../domain/branding';

const CACHE_KEY = 'capsule_branding';
/** How long a screen waits for the answer before drawing the built-in marks. */
const GIVE_UP_MS = 4000;
const BUILT_IN_TITLE = 'كبسولة - الرعاية الصحية';

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (summary) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(summary));
  } catch {
    /* storage unavailable — the next load asks again */
  }
};

const cached = readCache();
let state = { ready: Boolean(cached), ...resolveBranding(cached, API_BASE_URL) };
const listeners = new Set();
let loading = null;

function publish(summary, ready = true) {
  state = { ready, ...resolveBranding(summary, API_BASE_URL) };
  if (typeof document !== 'undefined') {
    // Only the shell's own title follows the name; a page that set its own keeps it.
    const title = state.name === BUILT_IN.name ? BUILT_IN_TITLE : state.name ? `${state.name} - الرعاية الصحية` : 'الرعاية الصحية';
    if (document.title.endsWith('الرعاية الصحية')) document.title = title;
  }
  listeners.forEach((fn) => fn());
}

/** Ask the server, at most once at a time. `force` asks again after a change. */
export function loadBranding({ force = false } = {}) {
  if (loading && !force) return loading;
  const giveUp = setTimeout(() => {
    if (!state.ready) publish(readCache());
  }, GIVE_UP_MS);
  loading = brandingApi.get()
    .then((summary) => {
      writeCache(summary);
      publish(summary);
    })
    .catch(() => {
      if (!state.ready) publish(readCache());
    })
    .finally(() => clearTimeout(giveUp));
  return loading;
}

/** Draw what the Super Admin just saved, without waiting for a reload. */
export function applyBranding(summary) {
  writeCache(summary);
  publish(summary);
}

/** The branding as last known, outside React (the print service). */
export const currentBranding = () => state;

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const snapshot = () => state;

/**
 * `{ ready, name, custom, agency: { src, alt, backdrop, custom }, product: {…} }`.
 * While `ready` is false nothing is known yet: draw the space, not a mark.
 */
export function useBranding() {
  useEffect(() => {
    loadBranding();
  }, []);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
